/**
 * SettingsSlide - Slide 3
 * Training parameters grid and augmentation options
 */

import { SettingSlider } from "../components/SettingSlider";
import { AugmentationToggle } from "../components/AugmentationToggle";
import type { TrainingSettings, AugmentationSettings } from "../types";

interface SettingsSlideProps {
  settings: TrainingSettings;
  onUpdateSettings: (settings: Partial<TrainingSettings>) => void;
  onUpdateAugmentation: (settings: Partial<AugmentationSettings>) => void;
}

export function SettingsSlide({
  settings,
  onUpdateSettings,
  onUpdateAugmentation,
}: SettingsSlideProps) {
  // Format learning rate display
  const formatLearningRate = (lr: number) => {
    return `${lr.toFixed(4)} \u00b7 COSINE`;
  };

  // Format rank display
  const formatRank = (rank: number) => {
    let quality = "BALANCED";
    if (rank <= 8) quality = "FAST";
    else if (rank >= 48) quality = "HIGH DETAIL";
    return `${rank} \u00b7 ${quality}`;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-3">
          Training settings
        </h2>
        <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
          Adjust training parameters to optimize for quality, speed, or cost.
          The defaults work well for most character LoRAs.
        </p>
      </div>

      {/* Settings grid - 2x2 */}
      <div className="grid grid-cols-2 gap-4 max-w-3xl mb-8">
        {/* Rank */}
        <SettingSlider
          label="Rank"
          value={settings.rank}
          min={4}
          max={64}
          step={4}
          displayValue={formatRank(settings.rank)}
          subtitle="Model capacity (higher = more detail)"
          onChange={(value) => onUpdateSettings({ rank: value })}
          markers={[16, 32, 48]}
        />

        {/* Epochs */}
        <SettingSlider
          label="Epochs"
          value={settings.epochs}
          min={8}
          max={80}
          step={4}
          displayValue={`${settings.epochs} PASSES`}
          subtitle="Training iterations"
          onChange={(value) => onUpdateSettings({ epochs: value })}
          markers={[32, 48, 64]}
        />

        {/* Learning Rate */}
        <SettingSlider
          label="Learning Rate"
          value={settings.learningRate}
          min={0.0001}
          max={0.002}
          step={0.0001}
          displayValue={formatLearningRate(settings.learningRate)}
          subtitle="How fast the model adapts"
          onChange={(value) => onUpdateSettings({ learningRate: value })}
          markers={[0.0004, 0.0008, 0.0012]}
        />

        {/* Snapshot */}
        <SnapshotSelector
          value={settings.snapshotEvery}
          onChange={(value) => onUpdateSettings({ snapshotEvery: value })}
        />
      </div>

      {/* Augmentation section */}
      <div className="max-w-3xl">
        <div className="mb-4">
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider">
            Augmentation
          </h3>
          <p className="text-[10px] text-muted-foreground/60 mt-0.5">
            Variation injected during training
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <AugmentationToggle
            label="Horizontal flip"
            enabled={settings.augmentation.horizontalFlip}
            onToggle={() =>
              onUpdateAugmentation({
                horizontalFlip: !settings.augmentation.horizontalFlip,
              })
            }
          />
          <AugmentationToggle
            label="Crop jitter"
            enabled={settings.augmentation.cropJitter}
            onToggle={() =>
              onUpdateAugmentation({
                cropJitter: !settings.augmentation.cropJitter,
              })
            }
          />
          <AugmentationToggle
            label="Colour jitter"
            enabled={settings.augmentation.colourJitter}
            onToggle={() =>
              onUpdateAugmentation({
                colourJitter: !settings.augmentation.colourJitter,
              })
            }
          />
          <AugmentationToggle
            label="Noise"
            enabled={settings.augmentation.noise}
            onToggle={() =>
              onUpdateAugmentation({
                noise: !settings.augmentation.noise,
              })
            }
          />
        </div>
      </div>
    </div>
  );
}

// Snapshot selector component - slider with discrete snaps
interface SnapshotSelectorProps {
  value: 1 | 2 | 4 | 8 | 16 | "final";
  onChange: (value: 1 | 2 | 4 | 8 | 16 | "final") => void;
}

type SnapshotValue = 1 | 2 | 4 | 8 | 16 | "final";

const SNAPSHOT_OPTIONS: Array<{ value: SnapshotValue; label: string }> = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 4, label: "4" },
  { value: 8, label: "8" },
  { value: 16, label: "16" },
  { value: "final", label: "Final" },
];

function SnapshotSelector({ value, onChange }: SnapshotSelectorProps) {
  // Map value to index position (0-5)
  const currentIndex = SNAPSHOT_OPTIONS.findIndex((opt) => opt.value === value);
  const percentage = (currentIndex / (SNAPSHOT_OPTIONS.length - 1)) * 100;

  // Get display value
  const displayValue =
    value === "final" ? "FINAL ONLY" : `EVERY ${value} EPOCHS`;

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value, 10);
    onChange(SNAPSHOT_OPTIONS[index].value);
  };

  return (
    <div className="rounded-[8px] border border-white/10 bg-[#1a1a1a] p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-xs text-muted-foreground uppercase tracking-wider">
            Snapshots
          </h4>
          <p className="text-[10px] text-muted-foreground/60 mt-0.5">
            Checkpoints during training
          </p>
        </div>
        <span className="text-sm font-mono text-foreground">
          {displayValue}
        </span>
      </div>

      {/* Slider track */}
      <div className="relative h-8">
        {/* Background track */}
        <div className="absolute top-1/2 left-0 right-0 h-1.5 -translate-y-1/2 rounded-full bg-white/10" />

        {/* Filled track */}
        <div
          className="absolute top-1/2 left-0 h-1.5 -translate-y-1/2 rounded-full bg-primary transition-all"
          style={{ width: `${percentage}%` }}
        />

        {/* Snap point markers - vertical lines */}
        {SNAPSHOT_OPTIONS.map((_, index) => {
          const markerPercent = (index / (SNAPSHOT_OPTIONS.length - 1)) * 100;
          return (
            <div
              key={index}
              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-white/20 rounded-full"
              style={{ left: `${markerPercent}%` }}
            />
          );
        })}

        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-2 border-white shadow-lg cursor-pointer transition-all"
          style={{ left: `${percentage}%` }}
        />

        {/* Invisible input for interaction */}
        <input
          type="range"
          min={0}
          max={SNAPSHOT_OPTIONS.length - 1}
          step={1}
          value={currentIndex}
          onChange={handleSliderChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>

      {/* Labels below slider */}
      <div className="relative mt-2 h-4">
        {SNAPSHOT_OPTIONS.map((option, index) => {
          const markerPercent = (index / (SNAPSHOT_OPTIONS.length - 1)) * 100;
          const isFirst = index === 0;
          const isLast = index === SNAPSHOT_OPTIONS.length - 1;
          return (
            <span
              key={option.value}
              className={`absolute text-[10px] transition-colors ${
                index === currentIndex
                  ? "text-foreground font-medium"
                  : "text-muted-foreground/60"
              }`}
              style={{
                left: `${markerPercent}%`,
                transform: isFirst
                  ? "none"
                  : isLast
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
              }}
            >
              {option.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
